import {gql} from '@apollo/client';

import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {Organization} from 'orgs/OrganizationConstants';
import EntityStore from 'stores/EntityStore';
import {OptionData} from 'ui-library/menus/MenuConstants';

import {
  ReviewLifecycleFilterFormQuery,
  ReviewLifecycleFilterFormQueryVariables,
} from './__generated/ReviewLifecycleFilterFormQuery';

type OrganizationOrInaccessible = ReviewLifecycleFilterFormQuery['organizations'][number];

type OrganizationNode = ExtractByTypename<OrganizationOrInaccessible, 'OrganizationNode'>;

type ReviewLifecycleNode = OrganizationNode['reviewLifecycles'][number];

export const REVIEW_LIFECYCLE_FILTER_FORM_QUERY = gql`
  query ReviewLifecycleFilterFormQuery($organizationIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: SIGHTGLASS) {
    organizations(ids: $organizationIds) {
      ... on OrganizationNode {
        id
        name
        reviewLifecycles {
          id
          archiveRecord {
            purpose
          }
          currentVersion {
            id
            name
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const getReviewLifecycleOptionDatas = (reviewLifecycles: Array<ReviewLifecycleNode>): Array<OptionData> =>
  reviewLifecycles.map((rlc) => ({
    value: rlc.id,
    name: rlc.currentVersion.name,
  }));

function queryDataToReviewLifecycles(organizationsOrInaccessibleInfos: Array<OrganizationOrInaccessible>): {
  reviewLifecycles: Array<ReviewLifecycleNode>;
} {
  const organizations = organizationsOrInaccessibleInfos.filter(
    (organizationOrInaccessible): organizationOrInaccessible is OrganizationNode =>
      !isInaccessible(organizationOrInaccessible)
  );
  return {
    reviewLifecycles: organizations
      .map((organization) => organization.reviewLifecycles)
      .flat()
      .filter((rlc) => rlc.archiveRecord == null),
  };
}

export async function getReviewLifecycleMenuOptionDatas(): Promise<Array<OptionData>> {
  const {organizations: organizationsOrInaccessibleInfos} = await doQuerySafe<
    ReviewLifecycleFilterFormQuery,
    ReviewLifecycleFilterFormQueryVariables
  >(
    {query: REVIEW_LIFECYCLE_FILTER_FORM_QUERY},
    {
      variables: {
        organizationIds: EntityStore.currentUser().organizations.map(
          (organization: Organization) => organization.id
        ),
      },
    }
  );
  const {reviewLifecycles} = queryDataToReviewLifecycles(organizationsOrInaccessibleInfos);
  return getReviewLifecycleOptionDatas(reviewLifecycles);
}
